import { Button } from '@design-systems/canvas-ui'
import Icon, {
  CloseIcon,
  FileTextIcon,
  MediaPlayIcon,
  PaperPlaneIcon,
  TimeClockIcon,
  WarningIcon,
} from '@design-systems/react-icons'
import {
  DOCUMENT_STATUS,
  getDocumentStatusIcon,
  getDocumentStatusTone,
  isDocumentApproved,
  isDocumentUploaded,
} from '../config/brandDetailsConfig'
import PublishUndoNotice from './PublishUndoNotice'
import { REPUBLISH_COPY } from '../config/brandLifecycleConfig'
import { MAX_VISIBLE_FILES, RAIL_STAGE } from '../config/documentRailConfig'
import FileListPopover from './FileListPopover'
import { PUBLISH_QUEUE_COPY } from '../config/draftReviewConfig'
import {
  countUploadedDocuments,
  getRailStatusLabel,
} from '../helpers/documentRailHelpers'

const StatusItem = ({
  deletingId,
  isDraftBrand,
  item,
  onDeleteDocument,
  stage,
}) => {
  const tone = getDocumentStatusTone(item.status)
  const statusIcon = getDocumentStatusIcon(item.status)
  // Documents uploaded against this type, already newest-first from the mapper.
  // Only documents still waiting for extraction; the Knowledge Base lists
  // them all.
  const documents = (item.documents || []).filter(
    ({ fileName, status }) => fileName && status === DOCUMENT_STATUS.UPLOADED,
  )
  const hiddenCount = Math.max(documents.length - MAX_VISIBLE_FILES, 0)

  return (
    <li className="document-status-rail__item">
      <span
        className={`document-status-rail__indicator document-status-rail__indicator--${tone}`}
      >
        {statusIcon && <Icon size="sm" svg={statusIcon} />}
      </span>
      <div>
        <strong>{item.label}</strong>
        <small className={`document-status-rail__status--${tone}`}>
          {getRailStatusLabel({ item, stage })}
        </small>
        {documents.length > 0 && (
          <ul className="document-status-rail__files">
            {documents.slice(0, MAX_VISIBLE_FILES).map((document) => (
              <li key={document.id} title={document.fileName}>
                <Icon size="sm" svg={FileTextIcon} />
                <span>{document.fileName}</span>
                {isDraftBrand && (
                  <button
                    aria-label={`Delete ${document.fileName}`}
                    className="document-status-rail__file-delete"
                    disabled={deletingId === document.id}
                    onClick={() => onDeleteDocument?.(document)}
                    type="button"
                  >
                    <Icon size="sm" svg={CloseIcon} />
                  </button>
                )}
              </li>
            ))}
            {hiddenCount > 0 && (
              <li className="document-status-rail__files-trigger">
                <FileListPopover
                  documents={documents}
                  label={`+${hiddenCount} more`}
                />
              </li>
            )}
          </ul>
        )}
      </div>
    </li>
  )
}

// One button per stage, in the order the brand passes through them.
const RailFooter = ({
  canRecover,
  canSendToPublishQueue,
  documentCount,
  isExtracting,
  isPublishing,
  isCheckingPublication,
  isRetryAvailable,
  isReviewReady,
  isCancellingPublish,
  isRetryExhausted,
  publicationCheckFailed,
  isSending,
  onOpenReview,
  onPublishBrand,
  onRecoverPublication,
  onRunExtraction,
  onCancelPublish,
  onSendToPublishQueue,
  onUndoExpire,
  stage,
  undoEndsAt,
}) => {
  if (stage === RAIL_STAGE.NONE) return null

  // The current publication failed. Sending it again is the only thing on
  // offer, whatever the brand's lifecycle status says.
  if (stage === RAIL_STAGE.REPUBLISH) {
    // Out of retries: only an admin can recover it; everyone else is pointed
    // at one.
    if (isRetryExhausted) {
      return (
        <div className="document-status-rail__footer">
          <span>
            {canRecover ? REPUBLISH_COPY.recoverHint : REPUBLISH_COPY.exhausted}
          </span>
          {canRecover && (
            <Button
              className="document-status-rail__publish-button"
              isDisabled={isCheckingPublication}
              isLoading={isPublishing}
              onClick={onRecoverPublication}
              variant="primary"
            >
              {REPUBLISH_COPY.recoverLabel}
            </Button>
          )}
        </div>
      )
    }

    return (
      <div className="document-status-rail__footer">
        <span>{REPUBLISH_COPY.hint}</span>
        <Button
          className="document-status-rail__publish-button"
          isDisabled={isCheckingPublication}
          isLoading={isPublishing}
          onClick={onPublishBrand}
          variant="primary"
        >
          <span className="upload-documents__button-content">
            <Icon size="sm" svg={PaperPlaneIcon} /> {REPUBLISH_COPY.label}
          </span>
        </Button>
      </div>
    )
  }

  if (stage === RAIL_STAGE.QUEUED) {
    return (
      <div className="document-status-rail__queued-footer">
        <strong>
          <Icon size="sm" svg={WarningIcon} />
          {PUBLISH_QUEUE_COPY.queuedHeading}
        </strong>
        <span>{PUBLISH_QUEUE_COPY.queuedHint}</span>
        {/* Set while a just-sent publication can still be cancelled. */}
        {undoEndsAt && (
          <PublishUndoNotice
            endsAt={undoEndsAt}
            isCancelling={isCancellingPublish}
            onCancel={onCancelPublish}
            onExpire={onUndoExpire}
          />
        )}
      </div>
    )
  }

  if (stage === RAIL_STAGE.PUBLISH) {
    // Held back until /publications/current has confirmed the brand has not
    // been sent already.
    let hint = canSendToPublishQueue
      ? PUBLISH_QUEUE_COPY.readyHint
      : PUBLISH_QUEUE_COPY.blockedHint
    if (publicationCheckFailed) hint = PUBLISH_QUEUE_COPY.statusUnavailableHint
    if (isCheckingPublication) hint = PUBLISH_QUEUE_COPY.checkingHint
    return (
      <div className="document-status-rail__footer">
        <span>{hint}</span>
        <Button
          className="document-status-rail__publish-button"
          isDisabled={
            !canSendToPublishQueue ||
            isCheckingPublication ||
            publicationCheckFailed
          }
          isLoading={isSending}
          onClick={onSendToPublishQueue}
          variant="primary"
        >
          <span className="upload-documents__button-content">
            <Icon size="sm" svg={PaperPlaneIcon} /> Send to Publish Queue
          </span>
        </Button>
      </div>
    )
  }

  if (stage === RAIL_STAGE.REVIEW) {
    return (
      <div className="document-status-rail__extraction-footer">
        <Button
          isDisabled={!isReviewReady}
          onClick={onOpenReview}
          variant="primary"
        >
          <span className="upload-documents__button-content">
            <Icon size="sm" svg={TimeClockIcon} /> Review
            <span className="document-status-rail__button-count">
              {documentCount} docs
            </span>
          </span>
        </Button>
      </div>
    )
  }

  return (
    <div className="document-status-rail__extraction-footer">
      <Button
        isDisabled={stage === RAIL_STAGE.UPLOAD || isExtracting}
        isLoading={isExtracting}
        onClick={onRunExtraction}
        variant="primary"
      >
        <span className="upload-documents__button-content">
          <Icon size="sm" svg={MediaPlayIcon} />{' '}
          {isRetryAvailable ? 'Retry Extraction' : 'Run AI extraction'}
          <span className="document-status-rail__button-count">
            {documentCount} docs
          </span>
        </span>
      </Button>
    </div>
  )
}

const DocumentStatusView = ({
  canRecover = false,
  canSendToPublishQueue = false,
  deletingId = '',
  documentStatuses = [],
  isDraftBrand = false,
  isRetryAvailable = false,
  isExtracting = false,
  isPublishing = false,
  isCheckingPublication = false,
  // Extraction has finished and there are sections behind the button. The
  // brand's lifecycle status is the only thing that knows this.
  isReviewReady = false,
  isCancellingPublish = false,
  isRetryExhausted = false,
  isSending = false,
  onCancelPublish = () => {},
  onDeleteDocument,
  onOpenReview = () => {},
  onPublishBrand = () => {},
  onRecoverPublication = () => {},
  onRunExtraction = () => {},
  onSendToPublishQueue = () => {},
  onUndoExpire = () => {},
  publicationCheckFailed = false,
  stage = RAIL_STAGE.NONE,
  undoEndsAt,
}) => {
  const required = documentStatuses.filter(({ required: isRequired }) =>
    Boolean(isRequired),
  )
  const optional = documentStatuses.filter(
    ({ required: isRequired }) => !isRequired,
  )
  const approvedRequired = required.filter(({ status }) =>
    isDocumentApproved(status),
  ).length
  const uploadedRequired = required.filter(({ status }) =>
    isDocumentUploaded(status),
  ).length
  const documentCount = countUploadedDocuments(documentStatuses)

  return (
    <aside className="document-status-rail" aria-label="Document status">
      <div className="document-status-rail__header">
        <strong>Required Documents</strong>
        <span>
          {uploadedRequired} of {required.length} uploaded · {approvedRequired}{' '}
          approved
        </span>
      </div>
      {/* Only the type list scrolls, so the footer below it stays put however
          many documents a brand collects. */}
      <div className="document-status-rail__scroll">
        <ul>
          {required.map((item) => (
            <StatusItem
              deletingId={deletingId}
              item={item}
              key={item.id}
              isDraftBrand={isDraftBrand}
              onDeleteDocument={onDeleteDocument}
              stage={stage}
            />
          ))}
        </ul>
        <h3>Optional</h3>
        <ul>
          {optional.map((item) => (
            <StatusItem
              deletingId={deletingId}
              isDraftBrand={isDraftBrand}
              item={item}
              key={item.id}
              onDeleteDocument={onDeleteDocument}
              stage={stage}
            />
          ))}
        </ul>
      </div>
      <RailFooter
        canRecover={canRecover}
        canSendToPublishQueue={canSendToPublishQueue}
        documentCount={documentCount}
        isExtracting={isExtracting}
        isPublishing={isPublishing}
        isCheckingPublication={isCheckingPublication}
        isReviewReady={isReviewReady}
        isRetryAvailable={isRetryAvailable}
        isCancellingPublish={isCancellingPublish}
        isRetryExhausted={isRetryExhausted}
        publicationCheckFailed={publicationCheckFailed}
        isSending={isSending}
        onCancelPublish={onCancelPublish}
        onOpenReview={onOpenReview}
        onPublishBrand={onPublishBrand}
        onRecoverPublication={onRecoverPublication}
        onRunExtraction={onRunExtraction}
        onSendToPublishQueue={onSendToPublishQueue}
        onUndoExpire={onUndoExpire}
        stage={stage}
        undoEndsAt={undoEndsAt}
      />
    </aside>
  )
}

export default DocumentStatusView
